
import { Component, EventEmitter, inject, Input, Output, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';

export interface SidebarNavItem {
  label: string;
  route?: string;
  href?: string;
  action?: () => void;
  external?: boolean;
  icon?: string; // SVG icon name
  key?: string; // Unique key for the item
}

export interface SidebarSocialIcon {
  icon: string;
  alt: string;
  url: string;
}

@Component({
  selector: 'app-sidebar',
  imports: [MatButtonModule, MatIconModule],
  templateUrl: './sidebar.html',
  styleUrl: './sidebar.scss',
})
export class Sidebar {
  @Input() navItems: SidebarNavItem[] = [];
  @Input() title?: string;
  @Input() width: string = '320px';
  @Input() position: 'left' | 'right' = 'left';
  @Input() closeOnNavigation: boolean = true;
  @Input() closeOnBackdropClick: boolean = true;
  @Input() showLocation: boolean = false;
  @Input() selectedCity?: string | null;
  @Input() selectedPincode?: string | null;
  @Input() locationLabel: string = 'To';
  @Input() showFollowUs: boolean = false;
  @Input() followUsTitle: string = 'FOLLOW US';
  @Input() socialIcons: SidebarSocialIcon[] = [];
  @Input() showHelpLegal: boolean = false;
  @Input() helpLegalTitle: string = 'HELP & LEGAL';
  @Input() helpLegalItems: SidebarNavItem[] = [];

  // User profile section
  @Input() showUserProfile: boolean = false;
  @Input() userName?: string | null;
  @Input() userPhone?: string | null;
  @Input() showWalletBalance: boolean = false;
  @Input() walletBalance?: number | null;
  @Input() walletLabel: string = 'Balance';
  @Input() showLogout: boolean = false;
  @Input() logoutLabel: string = 'Logout';
  @Input() navSectionTitle: string = 'IMPORTANT LINKS';

  @Output() closeSidebar = new EventEmitter<void>();
  @Output() navItemClick = new EventEmitter<SidebarNavItem>();
  @Output() locationClick = new EventEmitter<MouseEvent>();
  @Output() editProfileClick = new EventEmitter<void>();
  @Output() logoutClick = new EventEmitter<void>();

  protected readonly isOpen = signal<boolean>(false);
  private readonly router = inject(Router);

  // Check if a nav item is currently active based on URL
  protected isItemActive(item: SidebarNavItem): boolean {
    const currentUrl = this.router.url.split('?')[0]; // Remove query params

    // Check route match
    if (item.route) {
      // Exact match or starts with route
      if (currentUrl === item.route || currentUrl.startsWith(item.route + '/')) {
        return true;
      }
      // Check if last segment matches
      const routeSegments = item.route.split('/').filter((s) => s);
      const urlSegments = currentUrl.split('/').filter((s) => s);
      const lastRouteSegment = routeSegments[routeSegments.length - 1];
      const lastUrlSegment = urlSegments[urlSegments.length - 1];
      if (lastRouteSegment && lastUrlSegment && lastRouteSegment === lastUrlSegment) {
        return true;
      }
    }

    // Check key match in URL
    if (item.key) {
      // Convert key from underscore to hyphen format for URL matching
      const keyAsUrl = item.key.replace(/_/g, '-');
      if (currentUrl.includes(keyAsUrl) || currentUrl.includes(item.key)) {
        return true;
      }
    }

    return false;
  }

  open(): void {
    this.isOpen.set(true);
    // Prevent body scroll when sidebar is open
    if (typeof document !== 'undefined') {
      document.body.style.overflow = 'hidden';
    }
  }

  close(): void {
    this.isOpen.set(false);
    // Restore body scroll when sidebar is closed
    if (typeof document !== 'undefined') {
      document.body.style.overflow = '';
    }
    this.closeSidebar.emit();
  }

  handleNavItemClick(item: SidebarNavItem, event: Event): void {
    event.preventDefault();

    // Emit event for parent to handle
    this.navItemClick.emit(item);

    // Handle route navigation
    if (item.route) {
      this.router.navigate([item.route]).then(() => {
        if (this.closeOnNavigation) {
          this.close();
        }
      });
    }
    // Handle href navigation
    else if (item.href) {
      if (item.external) {
        window.open(item.href, '_blank');
      } else {
        window.location.href = item.href;
      }
      if (this.closeOnNavigation) {
        this.close();
      }
    }
    // Handle custom action
    else if (item.action) {
      item.action();
      if (this.closeOnNavigation) {
        this.close();
      }
    }
  }

  handleBackdropClick(event: MouseEvent): void {
    if (
      this.closeOnBackdropClick &&
      (event.target as HTMLElement).classList.contains('sidebar-backdrop')
    ) {
      this.close();
    }
  }

  handleLocationClick(event: MouseEvent): void {
    this.locationClick.emit(event);
  }

  handleEditProfile(): void {
    this.editProfileClick.emit();
    if (this.closeOnNavigation) {
      this.close();
    }
  }

  handleLogout(): void {
    this.logoutClick.emit();
    this.close();
  }
}
